import { useCreateContact } from "@jugl-web/domain-resources/contacts/hooks/useCreateContact";
import { ClientContact } from "@jugl-web/rest-api";
import { Dialog } from "@jugl-web/ui-components";
import { DrawerHeader } from "@jugl-web/ui-components/web/DrawerHeader";
import { Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import {
  ContactDetailsFormFields,
  ContactDetailsFormValues,
  EMPTY_CONTACT_DETAILS_FORM_VALUES,
} from "@web-src/modules/client-support/components/ContactDetailsFormFields";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as PeopleIcon } from "./assets/people.svg";

export interface NewContactDialogProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onContactCreated?: (contact: ClientContact) => void;
}

export const NewContactDialog: FC<NewContactDialogProps> = ({
  isOpen,
  onRequestClose,
  onContactCreated,
}) => {
  const { entityId } = useEntitySelectedProvider();

  const { createContact, isLoading: isCreatingContact } = useCreateContact({
    entityId,
  });

  const { t } = useTranslations();

  const form = useForm<ContactDetailsFormValues>({
    defaultValues: EMPTY_CONTACT_DETAILS_FORM_VALUES,
  });

  const { reset, handleSubmit } = form;

  const onSubmit = handleSubmit(async (values) => {
    const contact = await createContact({
      name: values.name,
      email: values.email,
      phone: values.phone,
    });

    if (contact) {
      onContactCreated?.(contact);
      onRequestClose();
    }
  });

  useEffect(() => {
    if (isOpen) {
      reset(EMPTY_CONTACT_DETAILS_FORM_VALUES);
    }
  }, [isOpen, reset]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className="flex max-h-[90vh] w-[780px] flex-col rounded-2xl"
    >
      <DrawerHeader
        type="title"
        title={
          <div className="flex items-center gap-3">
            <PeopleIcon className="shrink-0" />
            {t({
              id: "client-support-page.new-contact",
              defaultMessage: "New Customer",
            })}
          </div>
        }
        onClose={onRequestClose}
      />
      <FormProvider {...form}>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="flex flex-col gap-8 px-14 pt-10 pb-12">
            <div className="flex flex-col gap-1">
              <span className="font-secondary font-medium text-dark">
                {t({
                  id: "client-support-page.enter-contact-details",
                  defaultMessage: "Enter Customer Details",
                })}
              </span>
              <span className="font-secondary text-[13px] text-dark-600">
                {t({
                  id: "client-support-page.email-or-phone-required",
                  defaultMessage:
                    "An email or Phone number is required to create new customer",
                })}
              </span>
            </div>
            <ContactDetailsFormFields />
          </div>
          <div className="flex h-[72px] shrink-0 items-center justify-end gap-4 border-0 border-t border-solid border-dark-100 px-6">
            <Button
              variant="tertiary"
              className="w-[120px]"
              onClick={onRequestClose}
            >
              {t({
                id: "common.cancel",
                defaultMessage: "Cancel",
              })}
            </Button>
            <Button
              className="w-[300px]"
              iconEnd={<ArrowRightIcon />}
              onClick={onSubmit}
              isDisabled={isCreatingContact}
            >
              {t({
                id: "client-support-page.add-contact",
                defaultMessage: "Add Customer",
              })}
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
};
